import type { Vehicle } from "@fts/core";
import { useCallback, useEffect, useState } from "react";
import { ConfirmDialog, CopyButton } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { EmptyState, Panel } from "../components/ui";
import type { ChecklistFault, ChecklistSubmission, ChecklistSubmissionDetail } from "../data/provider";
import { useI18n } from "../i18n";
import { formatAlarmTime } from "../lib/alarms";
import { canManageFleet, isTenantAdmin } from "../lib/permissions";
import { sitePath } from "../lib/fleet";
import { useSession, type TenantData } from "../state/session";
import { FaultsTable } from "./ChecklistFaults";
import { LabelSheet, QrCode, ResultBadge, checklistUrl, itemText } from "./ChecklistParts";

/** Open critical faults make the vehicle show "Bakım gerekli" (ADR 0013); never a lock. */
export const needsMaintenance = (faults: ChecklistFault[], vehicleId: string): boolean =>
  faults.some((f) => f.vehicleId === vehicleId && f.status === "open" && f.critical);

/** "Bakım gerekli" for the vehicle header: an open critical fault exists (false while loading or on error). */
export function useMaintenanceRequired(vehicleId: string | null): boolean {
  const { provider, tenant } = useSession();
  const [flag, setFlag] = useState(false);
  const tenantId = tenant?.id;
  useEffect(() => {
    if (!tenantId || !vehicleId) return;
    let cancelled = false;
    provider
      .listChecklistFaults(tenantId, { vehicleId, status: "open" })
      .then((f) => !cancelled && setFlag(needsMaintenance(f, vehicleId)))
      .catch(() => !cancelled && setFlag(false));
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, vehicleId]);
  return flag;
}

/** Vehicle detail → "Kontrol listesi" tab: status, open faults, history with answers and photos, QR label. */
export function VehicleChecklistTab({ data, vehicle }: { data: TenantData; vehicle: Vehicle }) {
  const { t } = useI18n();
  const { provider, tenant } = useSession();
  const [state, setState] = useState<{ faults: ChecklistFault[]; submissions: ChecklistSubmission[] } | null>(null);
  const [error, setError] = useState(false);
  const tenantId = tenant?.id;

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [faults, submissions] = await Promise.all([
        provider.listChecklistFaults(tenantId, { vehicleId: vehicle.id, status: "open" }),
        provider.listChecklistSubmissions(tenantId, { vehicleId: vehicle.id, limit: 50 }),
      ]);
      setState({ faults, submissions });
      setError(false);
    } catch {
      setError(true);
    }
  }, [provider, tenantId, vehicle.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <EmptyState icon="alert" title={t("vehicleChecklist.loadError")} />;
  if (!state)
    return (
      <div className="loading" role="status">
        <span className="spinner" aria-hidden="true" />
        {t("common.loading")}
      </div>
    );
  const maintenance = needsMaintenance(state.faults, vehicle.id);
  return (
    <div className="stack-lg">
      {maintenance && (
        <div className="cl-maintenance" role="status">
          <Icon name="alert" size={18} />
          <div>
            <strong>{t("vehicleChecklist.maintenance")}</strong>
            <div className="small">{t("vehicleChecklist.maintenanceHint")}</div>
          </div>
        </div>
      )}
      <Panel title={t("vehicleChecklist.openFaults")} flush>
        {state.faults.length === 0 ? <p className="table-empty">{t("vehicleChecklist.noOpenFaults")}</p> : <FaultsTable data={data} faults={state.faults} showVehicle={false} onChanged={() => void load()} />}
      </Panel>
      <Panel title={t("vehicleChecklist.history")} flush>
        {state.submissions.length === 0 ? <p className="table-empty">{t("vehicleChecklist.empty")}</p> : <SubmissionsTable data={data} submissions={state.submissions} />}
      </Panel>
      <QrPanel data={data} vehicle={vehicle} />
    </div>
  );
}

function SubmissionsTable({ data, submissions }: { data: TenantData; submissions: ChecklistSubmission[] }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState<string | null>(null);
  const opName = (id: string | null) => (id ? (data.operators.operators.find((o) => o.id === id)?.fullName ?? t("common.empty")) : t("vehicleChecklist.operatorUnknown"));
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t("vehicleChecklist.when")}</th>
            <th scope="col" className="col-hide-sm">
              {t("vehicleChecklist.shift")}
            </th>
            <th scope="col" className="col-hide-sm">
              {t("vehicleChecklist.operator")}
            </th>
            <th scope="col">{t("vehicleChecklist.result")}</th>
            <th scope="col">
              <span className="sr-only">{t("vehicleChecklist.details")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => (
            <SubmissionRows key={s.id} s={s} open={open === s.id} onToggle={() => setOpen(open === s.id ? null : s.id)} operator={opName(s.operatorId)} when={formatAlarmTime(s.submittedAt, locale) ?? ""} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubmissionRows({ s, open, onToggle, operator, when }: { s: ChecklistSubmission; open: boolean; onToggle: () => void; operator: string; when: string }) {
  const { t } = useI18n();
  return (
    <>
      <tr>
        <td className="nowrap">
          {when}
          {s.impactSinceLast && <div className="muted small">{t("vehicleChecklist.impact")}</div>}
        </td>
        <td className="col-hide-sm">{s.shiftName ?? t("common.empty")}</td>
        <td className={`col-hide-sm${s.operatorUnknown ? " muted" : ""}`}>{operator}</td>
        <td>
          <ResultBadge result={s.result} />
        </td>
        <td>
          <button type="button" className="link-button" aria-expanded={open} onClick={onToggle}>
            {open ? t("vehicleChecklist.hideDetails") : t("vehicleChecklist.details")}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="cl-detail-row">
          <td colSpan={5}>
            <SubmissionDetail id={s.id} templateName={s.templateName} />
          </td>
        </tr>
      )}
    </>
  );
}

function SubmissionDetail({ id, templateName }: { id: string; templateName: string }) {
  const { t, lang } = useI18n();
  const { provider } = useSession();
  const [detail, setDetail] = useState<ChecklistSubmissionDetail | null | "error">(null);
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    provider
      .getChecklistSubmission(id)
      .then(async (d) => {
        if (cancelled) return;
        setDetail(d ?? "error");
        if (!d) return;
        const pairs = await Promise.all(d.photos.map(async (p) => [p.id, await provider.checklistPhotoUrl(p.path).catch(() => "")] as const));
        if (!cancelled) setUrls(Object.fromEntries(pairs.filter(([, u]) => u)));
      })
      .catch(() => !cancelled && setDetail("error"));
    return () => {
      cancelled = true;
    };
  }, [provider, id]);

  if (detail === "error") return <p className="form-error">{t("vehicleChecklist.loadError")}</p>;
  if (!detail) return <span className="spinner" aria-hidden="true" />;
  return (
    <div className="cl-detail">
      <div className="muted small">
        {t("vehicleChecklist.template")}: {templateName}
      </div>
      <ul className="cl-answers">
        {detail.answers.map((a) => {
          const photos = detail.photos.filter((p) => p.answerId === a.id);
          return (
            <li key={a.id} className={a.answer === "not_ok" ? "is-failed" : ""}>
              <span className={`badge tone-${a.answer === "ok" ? "ok" : a.answer === "na" ? "neutral" : a.critical ? "crit" : "warn"}`}>{t(`checklist.answer.${a.answer}`)}</span>
              <span className="mono muted">{a.code}</span> {itemText(lang, a)}
              {a.critical && a.answer === "not_ok" && <span className="badge tone-crit cl-inline-badge">{t("checklist.critical")}</span>}
              {a.note && <div className="cl-note">{a.note}</div>}
              {photos.length > 0 && (
                <div className="cl-photos" aria-label={t("vehicleChecklist.photos")}>
                  {photos.map((p, n) =>
                    urls[p.id] ? (
                      <a key={p.id} href={urls[p.id]} target="_blank" rel="noreferrer noopener">
                        <img src={urls[p.id]} alt={t("cf.photoAlt", { code: a.code, n: n + 1 })} />
                      </a>
                    ) : (
                      <span key={p.id} className="cl-photo-placeholder" />
                    ),
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function QrPanel({ data, vehicle }: { data: TenantData; vehicle: Vehicle }) {
  const { t } = useI18n();
  const { provider, tenant, role } = useSession();
  const [token, setToken] = useState<string | null | "none">(null);
  const [printing, setPrinting] = useState(false);
  const [rotating, setRotating] = useState(false);
  const tenantId = tenant?.id;
  const allowed = canManageFleet(role);

  useEffect(() => {
    if (!tenantId || !allowed) return;
    let cancelled = false;
    provider
      .listChecklistTokens(tenantId)
      .then((list) => !cancelled && setToken(list.find((x) => x.vehicleId === vehicle.id)?.token ?? "none"))
      .catch(() => !cancelled && setToken("none"));
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, vehicle.id, allowed]);

  const site = sitePath(data.sites, vehicle.siteId).map((s) => s.name).join(" / ");
  return (
    <Panel title={t("checklistQr.title")}>
      {!allowed ? (
        <p className="panel-text">{t("checklistQr.noAccess")}</p>
      ) : token === null ? (
        <span className="spinner" aria-hidden="true" />
      ) : token === "none" ? (
        <p className="panel-text">{t("checklistQr.noAccess")}</p>
      ) : (
        <div className="cl-qr">
          <QrCode value={checklistUrl(token)} size={140} label={t("checklistQr.title")} />
          <div className="stack">
            <p className="panel-text">{t("checklistQr.hint")}</p>
            <div className="cl-qr-actions">
              <button type="button" className="button button-secondary button-sm" onClick={() => setPrinting(true)}>
                <Icon name="download" size={15} />
                {t("checklistQr.print")}
              </button>
              <CopyButton text={checklistUrl(token)} label={t("checklistQr.copy")} />
              {isTenantAdmin(role) && (
                <button type="button" className="button button-secondary button-sm button-danger-text" onClick={() => setRotating(true)}>
                  <Icon name="refresh" size={15} />
                  {t("checklistQr.rotate")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {printing && typeof token === "string" && token !== "none" && (
        <LabelSheet title={t("checklistQr.sheetTitle", { site })} entries={[{ fleetNo: vehicle.fleetNo, siteName: site, token }]} onClose={() => setPrinting(false)} />
      )}
      {rotating && (
        <ConfirmDialog
          title={t("checklistQr.rotateTitle")}
          confirmLabel={t("checklistQr.rotate")}
          danger
          onClose={() => setRotating(false)}
          onConfirm={async () => {
            setToken(await provider.rotateChecklistToken(vehicle.id));
            setRotating(false);
          }}
        >
          {t("checklistQr.rotateText")}
        </ConfirmDialog>
      )}
    </Panel>
  );
}
